import { ArrowDown } from 'lucide-react';

import { AcademicPartners, Agents, LearningCenterPartners } from '@/components/partners';
import {
  MediaFrame,
  NavyAtmosphere,
  PointerParallax,
  ScrollReveal,
  SplitReveal,
  TextStagger,
  TiltCard,
} from '@/components/motion';
import { EnquiryForm } from '@/components/sections';
import { PARTNER_TIERS, PARTNERS_LEDE } from '@/data/partners';
import { Button } from '@ibas/ui';

/**
 * Hero still: a real IBAS DBA graduate being presented with a gold sash. A
 * portrait orientation is used so the figure fills the 4/5 hero plate under
 * object-cover. A still is used deliberately — the page carries its motion
 * through the sections rather than an autoplaying hero loop.
 */
const HERO_POSTER = '/media/images/graduation-events/graduation-dba-sash-portrait.webp';

/**
 * PartnersPage (/partners) — "Partners". A navy, motion-led hero opens the
 * page, then the three requested blocks follow in order: Academic Partners ·
 * Learning Center Partners · Agents. The shared enquiry form closes the page.
 *
 * Each in-page section is anchored (id + scroll-mt) so the mega-nav can
 * deep-link to it. Every section is a self-contained, reduced-motion-safe
 * component reading only verified facts from @/data/partners.
 */
export function PartnersPage() {
  return (
    <div className="bg-background">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative isolate flex min-h-[88vh] items-center overflow-hidden bg-brand-navy">
        <NavyAtmosphere />

        <div className="relative z-10 mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 px-6 py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          <div className="max-w-xl">
            <TextStagger
              text="Partners"
              className="font-label text-xs tracking-[0.24em] text-brand-gold-light uppercase"
            />
            <SplitReveal
              as="h1"
              by="lines"
              className="mt-4 font-display text-4xl leading-[1.05] text-white md:text-6xl"
            >
              A global network for Swiss-quality education.
            </SplitReveal>
            <ScrollReveal from="up" delay={0.1}>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-white/70">{PARTNERS_LEDE}</p>
            </ScrollReveal>
            <ScrollReveal from="up" delay={0.2}>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
                  asChild
                >
                  <a href="#academic-partners">
                    Explore our partners
                    <ArrowDown aria-hidden className="size-4" />
                  </a>
                </Button>
              </div>
            </ScrollReveal>

            {/* Tier quick-links — deep jump into each section. */}
            <ScrollReveal from="up" delay={0.3} className="mt-10 flex flex-wrap gap-2.5">
              {PARTNER_TIERS.map((tier) => (
                <a
                  key={tier.key}
                  href={tier.href}
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm font-medium text-white/85 transition-colors hover:border-brand-gold/50 hover:text-white"
                >
                  <tier.Icon aria-hidden className="size-4 text-brand-gold-light" />
                  {tier.label}
                </a>
              ))}
            </ScrollReveal>
          </div>

          {/* Hero media plate — pointer parallax over a tilt-reactive frame. */}
          <PointerParallax strength={16} className="mx-auto w-full max-w-md lg:max-w-none">
            <TiltCard className="rounded-2xl" glare>
              <div data-parallax data-depth="0.8">
                <MediaFrame
                  image={HERO_POSTER}
                  alt="An IBAS DBA graduate in cap and gown being presented with a gold DBA sash at the graduation ceremony."
                  aspect="aspect-[4/5]"
                  overlay={55}
                  rounded="rounded-2xl"
                  position="50% 25%"
                >
                  <div className="absolute inset-x-6 bottom-6 md:inset-x-8 md:bottom-8">
                    <span className="font-label text-xs tracking-[0.2em] text-brand-gold-light uppercase">
                      Study Your Way. Connect Globally.
                    </span>
                    <p className="mt-1 font-display text-2xl leading-tight text-white md:text-3xl">
                      Partners across four continents.
                    </p>
                  </div>
                </MediaFrame>
              </div>
            </TiltCard>
          </PointerParallax>
        </div>
      </section>

      {/* ── 1 · ACADEMIC PARTNERS ─────────────────────────────────────────── */}
      <div id="academic-partners" className="scroll-mt-24">
        <AcademicPartners />
      </div>

      {/* ── 2 · LEARNING CENTER PARTNERS ──────────────────────────────────── */}
      <div id="learning-center-partners" className="scroll-mt-24">
        <LearningCenterPartners />
      </div>

      {/* ── 3 · AGENTS ────────────────────────────────────────────────────── */}
      <div id="agents" className="scroll-mt-24">
        <Agents />
      </div>

      {/* Shared enquiry form closes the page (target of the partnering CTA). */}
      <div id="enquire" className="scroll-mt-24">
        <EnquiryForm />
      </div>
    </div>
  );
}
